import { isArray } from 'lodash';
import React, { useContext, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

const ADMIN_USER_RIGHTS_CONTEXT = React.createContext(null);

const useAdminUserRightsContext = () => useContext(ADMIN_USER_RIGHTS_CONTEXT);

const SUPER_ADMIN = 'super_admin';
const TEMPLATE = 'template';
const DOCUMENT = 'document';
const RECORDING = 'recording';
const SETTINGS = 'settings';
const USER = 'user';

const WILDCARD = '*';

const isAccessible = (permissions, id) => {
    if (!permissions?.length) {
        return false;
    }
    if (!id) {
        return true;
    }
    return permissions.find((e) => e === WILDCARD || e === id) !== undefined;
};

const AdminUserRightsProvider = ({ children }) => {
    const adminUserViewPermissions = useSelector((state) => state.user.informations.permissions);
    const isSuperAdmin = useSelector((state) => state.user.super_admin);

    const canRead = useCallback(
        (view, id) => {
            if (isSuperAdmin) {
                return true;
            }

            if (isArray(view)) {
                return !!view.find((v) => canRead(v, id));
            }

            switch (view) {
                case TEMPLATE:
                    return isAccessible(adminUserViewPermissions.template?.read, id);
                case DOCUMENT:
                    return isAccessible(adminUserViewPermissions.document?.read, id);
                case RECORDING:
                    return isAccessible(adminUserViewPermissions.recording?.read, id);
                case SETTINGS:
                    return isAccessible(adminUserViewPermissions.settings?.read, id);
                case USER:
                    return isAccessible(adminUserViewPermissions.user?.read, id);
                default:
                    return false;
            }
        },
        [adminUserViewPermissions],
    );

    const canList = useCallback(
        (view) => {
            if (isSuperAdmin) {
                return true;
            }

            if (isArray(view)) {
                return !!view.find((v) => canList(v));
            }

            switch (view) {
                case TEMPLATE:
                    return adminUserViewPermissions.template?.list?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case DOCUMENT:
                    return adminUserViewPermissions.document?.list?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case RECORDING:
                    return adminUserViewPermissions.recording?.list?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case SETTINGS:
                    return adminUserViewPermissions.settings?.list?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case USER:
                    return adminUserViewPermissions.user?.list?.length > 0; // Simplification for now, I should in fact only need a boolean here

                default:
                    return false;
            }
        },
        [adminUserViewPermissions],
    );

    const canWrite = useCallback(
        (view, id) => {
            if (isSuperAdmin) {
                return true;
            }
 
            if (isArray(view)) {
                return !!view.find((v) => canWrite(v, id));
            }

            switch (view) {
                case TEMPLATE:
                    return isAccessible(adminUserViewPermissions.template?.write, id);
                case DOCUMENT:
                    return isAccessible(adminUserViewPermissions.document?.write, id);
                case RECORDING:
                    return isAccessible(adminUserViewPermissions.collection?.write, id);
                case SETTINGS:
                        return isAccessible(adminUserViewPermissions.settings?.write, id);
                case USER:
                    return isAccessible(adminUserViewPermissions.user?.write, id);

                default:
                    return false;
            }
        },
        [adminUserViewPermissions],
    );

    const canCreate = useCallback(
        (view) => {
            if (isSuperAdmin) {
                return true;
            }

            if (isArray(view)) {
                return !!view.find((v) => canCreate(v));
            }

            switch (view) {
                case TEMPLATE:
                    return adminUserViewPermissions.template?.create?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case DOCUMENT:
                    return adminUserViewPermissions.document?.create?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case RECORDING:
                    return adminUserViewPermissions.recording?.create?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case SETTINGS:
                    return adminUserViewPermissions.settings?.create?.length > 0; // Simplification for now, I should in fact only need a boolean here
                case USER:
                    return adminUserViewPermissions.user?.create?.length > 0; // Simplification for now, I should in fact only need a boolean here
                default:
                    return false;
            }
        },
        [adminUserViewPermissions],
    );

    // TODO: Implement this
    const canDelete = useCallback(
        (view) => {
            if (isSuperAdmin) {
                return true;
            }

            return false;
        },
        [adminUserViewPermissions],
    );

    const value = useMemo(
        () => ({
            canRead,
            canWrite,
            canCreate,
            canList,
            canDelete,
        }),
        [canRead, canWrite, canCreate, canList],
    );

    return <ADMIN_USER_RIGHTS_CONTEXT.Provider value={value}>{children}</ADMIN_USER_RIGHTS_CONTEXT.Provider>;
};

export { useAdminUserRightsContext, SUPER_ADMIN, TEMPLATE, USER, DOCUMENT, RECORDING, SETTINGS };
export default AdminUserRightsProvider;
